/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { db, storage, functions } from "../../firebase";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import MonthlyReportsSettingsCard from "./MonthlyReportsSettingsCard";
import MonthlyReportsAdminTable from "./MonthlyReportsAdminTable";
import { getAdminBrandVars } from "../../utils/adminBranding";

/* =========================
   MONTHLY SETTINGS (EXISTING)
========================= */
export async function ensureMonthlyReportSettings(schoolId, uid) {
  if (!schoolId) return null;

  const refDoc = doc(db, "schools", schoolId, "settings", "monthlyReports");
  const snap = await getDoc(refDoc);

  const defaults = {
    enabled: true,
    parentDownloadEnabled: true,
    releaseDay: 1,
    downloadWindowDays: 7,
    includeCalendarView: true,
    includeSimpleSummary: true,
    updatedAt: serverTimestamp(),
    updatedBy: uid || null,
  };

  if (!snap.exists()) {
    await setDoc(refDoc, defaults, { merge: true });
    return defaults;
  }

  return { ...defaults, ...snap.data() };
}

/* =========================
   NEW: ABSENCE SETTINGS (PRODUCTION)
========================= */
export async function ensureAbsenceSettings(schoolId, uid) {
  if (!schoolId) return null;

  const refDoc = doc(db, "schools", schoolId, "settings", "absence");
  const snap = await getDoc(refDoc);

  const defaults = {
    allowEvidenceUploads: false,
    evidenceRetentionHoursAfterDecision: 72,
    evidenceHardDeleteDays: 7,
    updatedAt: serverTimestamp(),
    updatedBy: uid || null,
  };

  if (!snap.exists()) {
    await setDoc(refDoc, defaults, { merge: true });
    return defaults;
  }

  return { ...defaults, ...snap.data() };
}

/* =========================
   MAIN COMPONENT
========================= */
export default function SettingsSection({ schoolId, adminUid }) {
  const currentSchoolId = schoolId || "";
  const fileInputRef = useRef(null);

  /* =========================
     SCHOOL SETTINGS STATE
  ========================= */
  const [settingsDoc, setSettingsDoc] = useState({
    schoolName: "",
    email: "",
    phone: "",
    address: "",
    supportHours: "",
    timezone: "Europe/London",
    attendanceCutoff: "09:00",
    logoUrl: "",
    primaryColor: "#4F46E5",
    schoolId: currentSchoolId,
  });

  /* =========================
     NEW: ABSENCE STATE
  ========================= */
  const [absenceSettings, setAbsenceSettings] = useState({
    allowEvidenceUploads: false,
    evidenceRetentionHoursAfterDecision: 72,
    evidenceHardDeleteDays: 7,
  });

  /* =========================
     EXISTING STATES
  ========================= */
  const [manualMonthKey, setManualMonthKey] = useState(() => {
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const y = prev.getFullYear();
    const m = String(prev.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
  });

  const [runningManualReport, setRunningManualReport] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingAbsence, setSavingAbsence] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [isNarrow, setIsNarrow] = useState(
    typeof window !== "undefined" ? window.innerWidth < 980 : false
  );

  /* =========================
     LOAD SETTINGS
  ========================= */
  useEffect(() => {
    async function loadSettings() {
      setError("");
      setMessage("");

      try {
        const refDoc = doc(db, "schools", currentSchoolId, "settings", "school");
        const snap = await getDoc(refDoc);

        if (snap.exists()) {
          const data = snap.data() || {};
          setSettingsDoc({
            schoolName: data.schoolName || data.name || "",
            email: data.email || "",
            phone: data.phone || "",
            address: data.address || "",
            supportHours: data.supportHours || "",
            timezone: data.timezone || "Europe/London",
            attendanceCutoff: data.attendanceCutoff || "09:00",
            logoUrl: data.logoUrl || "",
            primaryColor: data.primaryColor || "#4F46E5",
            schoolId: data.schoolId || currentSchoolId,
          });
        }

        await ensureMonthlyReportSettings(currentSchoolId, adminUid);

        /* 🔥 NEW: LOAD ABSENCE SETTINGS */
        const absence = await ensureAbsenceSettings(currentSchoolId, adminUid);

        if (absence) {
          setAbsenceSettings({
            allowEvidenceUploads: !!absence.allowEvidenceUploads,
            evidenceRetentionHoursAfterDecision:
              absence.evidenceRetentionHoursAfterDecision || 72,
            evidenceHardDeleteDays:
              absence.evidenceHardDeleteDays || 7,
          });
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load settings.");
      }
    }

    loadSettings();
  }, [currentSchoolId, adminUid]);

  /* =========================
     SAVE SCHOOL SETTINGS
  ========================= */
  const saveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await setDoc(
        doc(db, "schools", currentSchoolId, "settings", "school"),
        {
          ...settingsDoc,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setMessage("Settings saved.");
    } catch (err) {
      console.error(err);
      setError("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  /* =========================
     🔥 SAVE ABSENCE SETTINGS
  ========================= */
  const saveAbsenceSettings = async () => {
    setSavingAbsence(true);

    try {
      await setDoc(
        doc(db, "schools", currentSchoolId, "settings", "absence"),
        {
          allowEvidenceUploads: absenceSettings.allowEvidenceUploads,
          evidenceRetentionHoursAfterDecision: Number(
            absenceSettings.evidenceRetentionHoursAfterDecision
          ),
          evidenceHardDeleteDays: Number(
            absenceSettings.evidenceHardDeleteDays
          ),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setMessage("Absence settings saved.");
    } catch (err) {
      console.error(err);
      setError("Failed to save absence settings.");
    } finally {
      setSavingAbsence(false);
    }
  };
    /* =========================
     MANUAL MONTHLY REPORT
  ========================= */
  const handleGenerateMonthlyReportsNow = async () => {
    if (!currentSchoolId) return;

    try {
      setRunningManualReport(true);
      setError("");
      setMessage("");

      const fn = httpsCallable(functions, "generateMonthlyAttendanceReportsManual");
      const res = await fn({
        schoolId: currentSchoolId,
        monthKey: manualMonthKey,
      });

      setMessage(
        `Monthly reports generated for ${manualMonthKey}. Total reports: ${
          res.data?.generatedCount || 0
        }.`
      );
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to generate monthly reports.");
    } finally {
      setRunningManualReport(false);
    }
  };

  /* =========================
     RESPONSIVE
  ========================= */
  useEffect(() => {
    const onResize = () => {
      setIsNarrow(window.innerWidth < 980);
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* =========================
     LOGO UPLOAD
  ========================= */
  const handleLogoUpload = async (file) => {
    if (!file || !currentSchoolId) return;

    try {
      setUploadingLogo(true);
      setError("");

      const safeName = String(file.name || "logo").replace(/\s+/g, "-");
      const storageRef = ref(
        storage,
        `schools/${currentSchoolId}/branding/logo-${Date.now()}-${safeName}`
      );

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      setSettingsDoc((prev) => ({
        ...prev,
        logoUrl: url,
      }));

      setMessage("Logo uploaded. Save settings to apply it.");
    } catch (err) {
      console.error(err);
      setError("Logo upload failed.");
    } finally {
      setUploadingLogo(false);
    }
  };

  const triggerFilePick = () => {
    fileInputRef.current?.click?.();
  };

  const layoutStyle = useMemo(
    () => ({
      ...formGrid,
      ...(isNarrow
        ? {
            gridTemplateColumns: "1fr",
            maxWidth: 860,
          }
        : {}),
    }),
    [isNarrow]
  );

if (!currentSchoolId) {
  return (
    <div style={shell}>
      <div style={card}>
        <div style={sectionTitle}>Loading school settings...</div>
        <div style={helperText}>Waiting for the correct school ID before loading settings.</div>
      </div>
    </div>
  );
}

return (
  <div
    style={{
      ...shell,
      ...getAdminBrandVars(settingsDoc.primaryColor || "#4F46E5"),
    }}
  >
      <div style={heroCard(settingsDoc.primaryColor)}>
        <div>
          <div style={eyebrow}>PresentPlus Admin</div>
          <h2 style={title}>Settings</h2>
          <p style={sub}>
            School identity, support details, branding, attendance rules,
            monthly report controls, and absence evidence settings.
          </p>
        </div>
      </div>

      {message ? <div style={successBanner}>{message}</div> : null}
      {error ? <div style={errorBanner}>{error}</div> : null}

      <form onSubmit={saveSettings} style={layoutStyle}>
        {/* LEFT COLUMN */}
        <div style={leftColumn}>
          <div style={card}>
            <div style={sectionTitle}>School Identity & Branding</div>

            <div style={fieldGroup}>
              <label style={label}>School Name</label>
              <input
                style={input}
                placeholder="Dallas Primary School"
                value={settingsDoc.schoolName}
                onChange={(e) =>
                  setSettingsDoc((p) => ({ ...p, schoolName: e.target.value }))
                }
              />
            </div>

            <div style={fieldGroup}>
              <label style={label}>School Email</label>
              <input
                style={input}
                placeholder="office@school.co.uk"
                value={settingsDoc.email}
                onChange={(e) =>
                  setSettingsDoc((p) => ({ ...p, email: e.target.value }))
                }
              />
            </div>

            <div style={fieldGroup}>
              <label style={label}>School Phone</label>
              <input
                style={input}
                placeholder="01234 567890"
                value={settingsDoc.phone}
                onChange={(e) =>
                  setSettingsDoc((p) => ({ ...p, phone: e.target.value }))
                }
              />
            </div>

            <div style={fieldGroup}>
              <label style={label}>School Address</label>
              <textarea
                style={textarea}
                placeholder="123 School Lane"
                value={settingsDoc.address}
                onChange={(e) =>
                  setSettingsDoc((p) => ({ ...p, address: e.target.value }))
                }
                rows={3}
              />
            </div>

            <div style={fieldGroup}>
              <label style={label}>School Logo URL</label>
              <input
                style={input}
                placeholder="https://yourcdn.com/logo.png"
                value={settingsDoc.logoUrl}
                onChange={(e) =>
                  setSettingsDoc((p) => ({ ...p, logoUrl: e.target.value }))
                }
              />
              <small style={helperText}>
                Used in email templates, sidebar branding, and parent-facing views.
              </small>
            </div>

            <div style={fieldGroup}>
              <label style={label}>Primary Brand Colour</label>
              <div style={colorRow}>
                <input
                  type="color"
                  value={settingsDoc.primaryColor || "#4F46E5"}
                  onChange={(e) =>
                    setSettingsDoc((p) => ({
                      ...p,
                      primaryColor: e.target.value,
                    }))
                  }
                  style={colorInput}
                />
                <input
                  style={input}
                  placeholder="#4F46E5"
                  value={settingsDoc.primaryColor}
                  onChange={(e) =>
                    setSettingsDoc((p) => ({
                      ...p,
                      primaryColor: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div style={fieldGroup}>
              <label style={label}>Upload School Logo</label>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                style={{ display: "none" }}
                onChange={(e) => handleLogoUpload(e.target.files?.[0])}
              />

              <div
                style={{
                  ...dropZone,
                  ...(dragOver ? dropZoneActive(settingsDoc.primaryColor) : {}),
                }}
                onClick={triggerFilePick}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const file = e.dataTransfer.files?.[0];
                  if (file) handleLogoUpload(file);
                }}
              >
                <div style={dropTitle}>
                  {uploadingLogo ? "Uploading logo..." : "Drag & drop logo here"}
                </div>
                <div style={dropSub}>or click to choose a file</div>
              </div>
            </div>

            {(settingsDoc.logoUrl || settingsDoc.schoolName) && (
              <div style={fieldGroup}>
                <label style={label}>Brand Preview</label>
                <div style={brandPreview}>
                  <div
                    style={{
                      ...brandPreviewLogoWrap,
                      background: "var(--pp-brand-gradient)",
                    }}
                  >
                    {settingsDoc.logoUrl ? (
                      <img
                        src={settingsDoc.logoUrl}
                        alt="School logo"
                        style={brandPreviewLogo}
                      />
                    ) : (
                      <span style={{ color: "#fff", fontWeight: 900 }}>PP</span>
                    )}
                  </div>

                  <div>
                    <div style={brandPreviewTitle}>
                      {settingsDoc.schoolName || "School Name"}
                    </div>
                    <div style={brandPreviewMeta}>
                      {settingsDoc.primaryColor || "#4F46E5"}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button
              style={button(settingsDoc.primaryColor)}
              type="submit"
              disabled={saving}
            >
              {saving ? "Saving..." : "Save School Settings"}
            </button>
          </div>

          {/* FIXED HEIGHT GENERATED MONTHLY REPORTS */}
          <div style={reportsShell}>
            <div>
              <div style={sectionTitle}>Generated Monthly Reports</div>
              <div style={helperText}>
                Fixed-height report history. Scroll inside this panel instead of
                stretching the whole settings page.
              </div>
            </div>

            <div style={reportsScroll}>
              <MonthlyReportsAdminTable schoolId={currentSchoolId} />
            </div>
          </div>


          {/* MANUAL REPORT GENERATOR */}
          <div style={card}>
            <div style={sectionTitle}>Manual Report Generator</div>

            <div style={fieldGroup}>
              <label style={label}>Month Key</label>
              <input
                style={input}
                placeholder="2026-04"
                value={manualMonthKey}
                onChange={(e) => setManualMonthKey(e.target.value)}
              />
              <small style={helperText}>
                Use format YYYY-MM to generate monthly attendance PDFs.
              </small>
            </div>

            <button
              type="button"
              style={button(settingsDoc.primaryColor)}
              onClick={handleGenerateMonthlyReportsNow}
              disabled={runningManualReport}
            >
              {runningManualReport
                ? "Generating..."
                : "Generate Monthly Reports Now"}
            </button>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div style={rightColumn}>
          <div style={card}>
            <div style={sectionTitle}>Parent Support & Attendance Rules</div>

            <div style={fieldGroup}>
              <label style={label}>Support Hours</label>
              <input
                style={input}
                placeholder="Mon–Fri 8:00–16:00"
                value={settingsDoc.supportHours}
                onChange={(e) =>
                  setSettingsDoc((p) => ({ ...p, supportHours: e.target.value }))
                }
              />
            </div>

            <div style={fieldGroup}>
              <label style={label}>Timezone</label>
              <input
                style={input}
                placeholder="Europe/London"
                value={settingsDoc.timezone}
                onChange={(e) =>
                  setSettingsDoc((p) => ({ ...p, timezone: e.target.value }))
                }
              />
            </div>

            <div style={fieldGroup}>
              <label style={label}>Attendance Cutoff</label>
              <input
                style={input}
                placeholder="09:00"
                value={settingsDoc.attendanceCutoff}
                onChange={(e) =>
                  setSettingsDoc((p) => ({
                    ...p,
                    attendanceCutoff: e.target.value,
                  }))
                }
              />
            </div>

            <div style={fieldGroup}>
              <label style={label}>Internal School ID</label>
              <input style={inputDisabled} value={currentSchoolId} disabled />
              <small style={helperText}>
                Internal system ID used for settings, invites, and school-level
                records.
              </small>
            </div>
          </div>

          {/* EMAIL PREVIEW */}
          <div style={card}>
            <div style={sectionTitle}>Email Preview</div>

            <div style={emailPreviewWrap}>
              <div
                style={{
                  ...emailHeader,
                  background: "var(--pp-brand-gradient)",
                }}
              >
                {settingsDoc.logoUrl ? (
                  <img
                    src={settingsDoc.logoUrl}
                    alt="School logo"
                    style={emailHeaderLogo}
                  />
                ) : null}

                <div style={emailKicker}>School Invitation</div>
                <div style={emailSchoolName}>
                  {settingsDoc.schoolName || "Your School"}
                </div>
                <div style={emailByline}>via PresentPlus</div>
              </div>

              <div style={emailBody}>
                <div style={emailTitle}>You’ve been invited to PresentPlus</div>
                <div style={emailText}>Hello Parent,</div>
                <div style={emailText}>
                  <strong>{settingsDoc.schoolName || "Your School"}</strong>{" "}
                  has invited you to access your child’s attendance and updates
                  through PresentPlus.
                </div>

                <button
                  type="button"
                  style={{
                    ...emailButton,
                    background: "var(--pp-brand-gradient)",
                  }}
                >
                  Accept Invitation
                </button>
              </div>
            </div>
          </div>


          {/* ABSENCE EVIDENCE SETTINGS */}
          <div style={card}>
            <div style={sectionTitle}>Absence Evidence Uploads</div>

            <div style={toggleRow}>
              <div>
                <div style={toggleTitle}>Allow parent evidence uploads</div>
                <div style={helperText}>
                  When enabled, parents can upload one optional supporting
                  document with an absence report.
                </div>
              </div>

              <label style={switchWrap}>
                <input
                  type="checkbox"
                  checked={absenceSettings.allowEvidenceUploads}
onChange={async (e) => {
  const value = e.target.checked;

  setAbsenceSettings((p) => ({
    ...p,
    allowEvidenceUploads: value,
  }));

  try {
    await setDoc(
      doc(db, "schools", currentSchoolId, "settings", "absence"),
      {
        allowEvidenceUploads: value,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    setMessage(
      value
        ? "Parent evidence uploads enabled."
        : "Parent evidence uploads disabled."
    );
  } catch (err) {
    console.error(err);
    setError("Failed to update setting.");
  }
}}
                  style={switchInput}
                />
                <span
                  style={{
                    ...switchTrack,
                    ...(absenceSettings.allowEvidenceUploads
                      ? switchTrackOn(settingsDoc.primaryColor)
                      : {}),
                  }}
                >
                  <span
                    style={{
                      ...switchDot,
                      ...(absenceSettings.allowEvidenceUploads
                        ? switchDotOn
                        : {}),
                    }}
                  />
                </span>
              </label>
            </div>

            <div style={fieldGroup}>
              <label style={label}>Delete after decision / review window</label>
              <input
                type="number"
                min="1"
                style={input}
                value={absenceSettings.evidenceRetentionHoursAfterDecision}
                onChange={(e) =>
                  setAbsenceSettings((p) => ({
                    ...p,
                    evidenceRetentionHoursAfterDecision: e.target.value,
                  }))
                }
              />
              <small style={helperText}>
                Recommended: 72 hours. This gives the school a short dispute
                window after decision.
              </small>
            </div>

            <div style={fieldGroup}>
              <label style={label}>Hard delete after days</label>
              <input
                type="number"
                min="1"
                style={input}
                value={absenceSettings.evidenceHardDeleteDays}
                onChange={(e) =>
                  setAbsenceSettings((p) => ({
                    ...p,
                    evidenceHardDeleteDays: e.target.value,
                  }))
                }
              />
              <small style={helperText}>
                Recommended: 7 days. This is the maximum retention even if no
                admin action happens.
              </small>
            </div>

            <div style={warningBox}>
              Evidence documents are temporary. Parents must not upload medical
              diagnoses or sensitive details. Files are auto-deleted after
              decision/review window or within the hard-delete limit. Only
              metadata remains.
            </div>

            <button
              type="button"
              style={button(settingsDoc.primaryColor)}
              onClick={saveAbsenceSettings}
              disabled={savingAbsence}
            >
              {savingAbsence ? "Saving..." : "Save Evidence Settings"}
            </button>
          </div>

          <MonthlyReportsSettingsCard
            schoolId={currentSchoolId}
            adminUid={adminUid}
          />

        </div>
      </form>
    </div>
  );
}
/* =========================
   STYLES
========================= */

const shell = {
  padding: "28px 28px 40px",
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
};

const heroCard = (color) => ({
  marginBottom: 24,
  padding: "30px 30px",
  borderRadius: 26,
  background: "var(--pp-brand-gradient)",
  color: "#fff",
  boxShadow: "0 20px 40px rgba(76,87,255,0.18)",
});

const eyebrow = {
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  opacity: 0.86,
  marginBottom: 8,
};

const title = {
  margin: 0,
  fontSize: 42,
  lineHeight: 1,
  letterSpacing: "-0.04em",
  fontWeight: 900,
};

const sub = {
  margin: "8px 0 0",
  maxWidth: 780,
  color: "rgba(255,255,255,0.92)",
  lineHeight: 1.6,
};

const formGrid = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.06fr) minmax(420px, 0.94fr)",
  gap: 24,
  width: "100%",
  maxWidth: "100%",
  alignItems: "start",
};

const leftColumn = {
  display: "grid",
  gap: 18,
  minWidth: 0,
};

const rightColumn = {
  display: "grid",
  gap: 18,
  minWidth: 0,
};

const card = {
  padding: 24,
  borderRadius: 22,
  background: "#fff",
  border: "1px solid rgba(23,28,58,0.06)",
  boxShadow: "0 10px 30px rgba(24,39,75,0.05)",
  display: "grid",
  gap: 14,
};

const sectionTitle = {
  fontSize: 17,
  fontWeight: 900,
  color: "#171c3a",
  marginBottom: 2,
};

const fieldGroup = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  minWidth: 0,
};

const label = {
  fontSize: 13,
  fontWeight: 800,
  color: "#2f3a68",
};

const input = {
  padding: "14px 16px",
  borderRadius: 14,
  border: "1px solid #ddd",
  background: "#fff",
  width: "100%",
  boxSizing: "border-box",
  color: "#171c3a",
};

const textarea = {
  padding: "14px 16px",
  borderRadius: 14,
  border: "1px solid #ddd",
  background: "#fff",
  width: "100%",
  boxSizing: "border-box",
  resize: "vertical",
  fontFamily: "inherit",
  color: "#171c3a",
};

const inputDisabled = {
  padding: "14px 16px",
  borderRadius: 14,
  border: "1px solid #ddd",
  background: "#f5f7fb",
  color: "#7a849f",
  width: "100%",
  boxSizing: "border-box",
};

const helperText = {
  color: "#7a849f",
  fontSize: 12,
  lineHeight: 1.5,
};

const colorRow = {
  display: "flex",
  gap: 10,
  alignItems: "center",
};

const colorInput = {
  width: 56,
  height: 42,
  padding: 4,
  borderRadius: 12,
  border: "1px solid #ddd",
  background: "#fff",
  flexShrink: 0,
};

const dropZone = {
  padding: "22px 18px",
  borderRadius: 18,
  border: "1px dashed rgba(76,87,255,0.25)",
  background: "rgba(76,87,255,0.04)",
  textAlign: "center",
  cursor: "pointer",
  transition: "all 180ms ease",
};

const dropZoneActive = (color) => ({
  border: `1px dashed ${color || "#4F46E5"}`,
  background: `${color || "#4F46E5"}12`,
  transform: "translateY(-1px)",
});

const dropTitle = {
  fontWeight: 900,
  color: "#1f2747",
};

const dropSub = {
  marginTop: 6,
  fontSize: 13,
  color: "#74809d",
};

const brandPreview = {
  display: "flex",
  alignItems: "center",
  gap: 14,
  padding: 14,
  borderRadius: 16,
  border: "1px solid rgba(23,28,58,0.06)",
  background: "#fff",
};

const brandPreviewLogoWrap = {
  width: 52,
  height: 52,
  borderRadius: 14,
  display: "grid",
  placeItems: "center",
  overflow: "hidden",
  flexShrink: 0,
};

const brandPreviewLogo = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const brandPreviewTitle = {
  fontWeight: 900,
  color: "#171c3a",
};

const brandPreviewMeta = {
  marginTop: 4,
  color: "#6e7896",
  fontSize: 13,
};

const reportsShell = {
  padding: 18,
  borderRadius: 22,
  background: "#fff",
  border: "1px solid rgba(23,28,58,0.06)",
  boxShadow: "0 10px 30px rgba(24,39,75,0.05)",
  display: "grid",
  gap: 14,
  alignContent: "start",
};

const reportsScroll = {
  height: 610,
  maxHeight: "68vh",
  overflowY: "auto",
  overflowX: "auto",
  borderRadius: 18,
  border: "1px solid #edf2fb",
  background: "#fbfdff",
};

const toggleRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 14,
};

const toggleTitle = {
  fontSize: 14,
  fontWeight: 900,
  color: "#171c3a",
  marginBottom: 4,
};

const switchWrap = {
  display: "inline-flex",
  alignItems: "center",
  cursor: "pointer",
  flexShrink: 0,
};

const switchInput = {
  display: "none",
};

const switchTrack = {
  width: 58,
  height: 32,
  borderRadius: 999,
  background: "#e2e8f0",
  border: "1px solid #dbe3ef",
  position: "relative",
  transition: "all 180ms ease",
};

const switchTrackOn = (color) => ({
  background: color || "#4F46E5",
  border: `1px solid ${color || "#4F46E5"}`,
});

const switchDot = {
  width: 26,
  height: 26,
  borderRadius: 999,
  background: "#fff",
  position: "absolute",
  top: 2,
  left: 2,
  transition: "all 180ms ease",
  boxShadow: "0 4px 10px rgba(15,23,42,0.16)",
};

const switchDotOn = {
  transform: "translateX(26px)",
};

const warningBox = {
  padding: "12px 14px",
  borderRadius: 16,
  background: "#fff7ed",
  color: "#9a3412",
  border: "1px solid #fed7aa",
  fontSize: 13,
  lineHeight: 1.5,
  fontWeight: 700,
};

const emailPreviewWrap = {
  borderRadius: 28,
  overflow: "hidden",
  border: "1px solid rgba(23,28,58,0.06)",
  background: "#fff",
};

const emailHeader = {
  padding: 22,
  color: "#fff",
  textAlign: "center",
};

const emailHeaderLogo = {
  maxHeight: 54,
  maxWidth: 150,
  objectFit: "contain",
  marginBottom: 12,
};

const emailKicker = {
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  opacity: 0.9,
};

const emailSchoolName = {
  marginTop: 8,
  fontSize: 28,
  fontWeight: 900,
};

const emailByline = {
  marginTop: 6,
  opacity: 0.9,
};

const emailBody = {
  padding: 24,
  display: "grid",
  gap: 14,
};

const emailTitle = {
  fontSize: 28,
  lineHeight: 1.1,
  fontWeight: 900,
  color: "#0f172a",
};

const emailText = {
  fontSize: 15,
  lineHeight: 1.7,
  color: "#475569",
};

const emailButton = {
  marginTop: 4,
  border: "none",
  color: "#fff",
  padding: "14px 18px",
  borderRadius: 14,
  fontWeight: 800,
  width: "fit-content",
};

const button = (color) => ({
  padding: "14px 16px",
  borderRadius: 14,
  border: "none",
  background: "var(--pp-brand-gradient)",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  boxShadow: "0 14px 26px var(--pp-brand-glow)",
});

const successBanner = {
  marginBottom: 14,
  padding: "12px 14px",
  borderRadius: 14,
  background: "rgba(132,196,65,0.14)",
  color: "#3d6f18",
  border: "1px solid rgba(132,196,65,0.25)",
  fontWeight: 800,
};

const errorBanner = {
  marginBottom: 14,
  padding: "12px 14px",
  borderRadius: 14,
  background: "#fff1f2",
  color: "#9f1239",
  border: "1px solid #fecdd3",
  fontWeight: 800,
};